// ─── UPLOAD CON DESTINAZIONE ONEDRIVE ────────────────────────────────────────
// Componente riutilizzabile da importare in tutte le pagine con allegati

import React, { useState, useEffect, useRef } from 'react';

export default function UploadConOneDrive({ 
  onUpload,        // function(file, cartellaOneDrive|null) - chiamata dopo upload
  endpoint,        // es. '/api/iva/123/allegati'
  fieldName = 'file',
  extraFields = {},
  label = '📎 Carica file',
  accept = '*/*',
  addToast,
}) {
  const [showModal, setShowModal] = useState(false);
  const [file, setFile] = useState(null);
  const [cartelleAnni, setCartelleAnni] = useState({});
  const [annoSel, setAnnoSel] = useState('');
  const [cartellaSel, setCartellaSel] = useState('');
  const [uploading, setUploading] = useState(false);
  const [usaOneDrive, setUsaOneDrive] = useState(true);
  const fileRef = useRef();

  const ANNI = ['2026','2025','2024','2023','2022'];

  const caricaCartelle = async (anno) => {
    if (cartelleAnni[anno]) return;
    const res = await fetch(`/api/onedrive/cartelle?anno=${anno}`);
    const j = await res.json();
    if (j.success) setCartelleAnni(p => ({ ...p, [anno]: j.data }));
  };

  useEffect(() => {
    if (annoSel) caricaCartelle(annoSel);
  }, [annoSel]);

  const handleFileChange = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    setFile(f);
    setShowModal(true);
    if (fileRef.current) fileRef.current.value = '';
  };

  const handleUpload = async () => {
    if (!file) return;
    setUploading(true);
    try {
      // 1. Carica il file sull'endpoint principale
      const fd = new FormData();
      fd.append(fieldName, file);
      Object.entries(extraFields).forEach(([k,v]) => fd.append(k, v));

      const res = await fetch(endpoint, { method: 'POST', body: fd });
      const j = await res.json();
      if (!j.success) throw new Error(j.error);

      const percorsoLocale = j.data?.filename || j.data?.relpath || '';

      // 2. Se selezionata cartella OneDrive, copia anche lì
      if (usaOneDrive && cartellaSel && percorsoLocale) {
        try {
          await fetch('/api/onedrive/copia-file', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              percorso_locale: percorsoLocale,
              cartella_onedrive: cartellaSel,
            }),
          });
          if (addToast) addToast(`File caricato + copiato su OneDrive in ${cartellaSel}`, 'success');
        } catch(e) {
          if (addToast) addToast('File caricato ma errore OneDrive: ' + e.message, 'error');
        }
      } else {
        if (addToast) addToast('File caricato', 'success');
      }

      if (onUpload) onUpload(j.data);
      setShowModal(false);
      setFile(null);
      setCartellaSel('');
    } catch(e) {
      if (addToast) addToast(e.message, 'error');
    }
    setUploading(false);
  };

  const cartelleFiltrate = annoSel ? (cartelleAnni[annoSel] || []) : [];

  return (
    <>
      <input ref={fileRef} type="file" accept={accept} style={{ display: 'none' }}
        onChange={handleFileChange} />
      <button className="btn btn-sm" onClick={() => fileRef.current.click()}>
        {label}
      </button>

      {showModal && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
          zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center',
        }} onClick={e => { if(e.target===e.currentTarget) setShowModal(false); }}>
          <div style={{
            background: 'var(--surface)', borderRadius: 12, padding: 24,
            width: 480, boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
          }}>
            <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 4 }}>📎 Carica file</div>
            <div style={{ fontSize: 13, color: 'var(--text-faint)', marginBottom: 20 }}>
              {file?.name}
            </div>

            {/* Toggle OneDrive */}
            <label style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16,
              padding: '10px 14px', background: 'var(--bg)', borderRadius: 8, cursor: 'pointer' }}>
              <input type="checkbox" checked={usaOneDrive}
                onChange={e => setUsaOneDrive(e.target.checked)}
                style={{ accentColor: 'var(--accent)', cursor: 'pointer', width: 16, height: 16 }} />
              <div>
                <div style={{ fontSize: 13, fontWeight: 600 }}>☁ Copia anche su OneDrive</div>
                <div style={{ fontSize: 11, color: 'var(--text-faint)' }}>
                  Il file verrà salvato anche nella cartella OneDrive selezionata
                </div>
              </div>
            </label>

            {usaOneDrive && (
              <div style={{ marginBottom: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
                {/* Selezione anno */}
                <div>
                  <label style={{ fontSize: 12, color: 'var(--text-muted)', fontWeight: 600,
                    textTransform: 'uppercase', letterSpacing: '0.4px', display: 'block', marginBottom: 6 }}>
                    Anno
                  </label>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {ANNI.map(a => (
                      <button key={a} onClick={() => { setAnnoSel(a); setCartellaSel(''); }}
                        style={{
                          padding: '5px 14px', fontSize: 13, borderRadius: 6, cursor: 'pointer',
                          border: `2px solid ${annoSel===a ? 'var(--accent)' : 'var(--border)'}`,
                          background: annoSel===a ? 'var(--accent-light)' : 'var(--surface)',
                          color: annoSel===a ? 'var(--accent)' : 'var(--text)',
                          fontWeight: annoSel===a ? 600 : 400,
                        }}>{a}</button>
                    ))}
                  </div>
                </div>

                {/* Selezione cartella */}
                {annoSel && (
                  <div>
                    <label style={{ fontSize: 12, color: 'var(--text-muted)', fontWeight: 600,
                      textTransform: 'uppercase', letterSpacing: '0.4px', display: 'block', marginBottom: 6 }}>
                      Cartella destinazione
                    </label>
                    {cartelleFiltrate.length === 0 ? (
                      <div style={{ fontSize: 12, color: 'var(--text-faint)', fontStyle: 'italic' }}>
                        ⏳ Caricamento cartelle…
                      </div>
                    ) : (
                      <select value={cartellaSel} onChange={e => setCartellaSel(e.target.value)}
                        style={{ width: '100%', padding: '8px 10px', fontSize: 13,
                          border: '1px solid var(--border)', borderRadius: 6 }}>
                        <option value="">— Seleziona cartella —</option>
                        {cartelleFiltrate.map(c => (
                          <option key={c} value={c}>{c}</option>
                        ))}
                      </select>
                    )}
                  </div>
                )}

                {cartellaSel && (
                  <div style={{ padding: '8px 12px', background: 'var(--accent-light)',
                    borderRadius: 6, fontSize: 12, color: 'var(--accent)' }}>
                    📁 Verrà copiato in: <strong>{cartellaSel}</strong>
                  </div>
                )}
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 8 }}>
              <button className="btn" onClick={() => { setShowModal(false); setFile(null); }}>
                Annulla
              </button>
              <button className="btn btn-primary" onClick={handleUpload}
                disabled={uploading || (usaOneDrive && !cartellaSel && annoSel)}>
                {uploading ? '⏳ Carico…' : '📤 Carica'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
